import React from 'react';
import { useParams } from 'react-router-dom';
import { Clock, ChevronLeft, ChevronRight, Zap } from 'lucide-react';
import CollapsibleSection from './shared/components/CollapsibleSection';
import CountdownTimer from './features/timeline/components/CountdownTimer';
import ScheduleChart from './features/timeline/components/ScheduleChart';
import TimeElementList from './features/timeline/components/TimeElementList';
import TopNav from './shared/components/TopNav';
import OpenInAppButton from './shared/components/OpenInAppButton';
import { useTimeline } from './features/timeline/context/TimelineContext';
import {
  parseTimeToSeconds,
  formatTo12Hour,
  calculateOccurrenceStartTimeFromElements,
} from '@thekwallace/backtime-shared';

/**
 * Main App component - renders the timeline UI
 * All state management is handled by TimelineContext
 */
const App: React.FC = () => {
  const { code: publicCode } = useParams<{ code?: string }>();

  const {
    // Loading states
    loading,
    error,
    setError,

    // Current timeline
    currentTimelineId,
    timelineName,
    isTimelinePublic,
    timelinePublicCode,
    isOwner,
    isSubscribed,

    // All timelines
    allTimelines,

    // Schedule
    schedule,
    setSchedule,
    selectedStartTime,
    timeOffset,
    setTimeOffset,
    activeStartTime,
    isAutoAdvanceEnabled,
    setIsAutoAdvanceEnabled,
    autoAdvanceBuffer,
    setAutoAdvanceBuffer,

    // Elements (flat array with sortOrder and anchorOffset)
    elements,

    // Computed values
    scheduledOccurrenceTimes,
    upcomingTimes,
    noUpcomingOccurrences,
    clientAutoAdvanceTime,
    effectiveStartTime,
    effectiveStartAbsolute,
    effectiveDayName,
    isOccurrencePast,

    // Timeline CRUD handlers
    handleTimelineSwitch,
    handleCreateNewTimeline,
    handleDeleteTimeline,
    handleTimelineNameChange,
    handleTogglePublic,
    handlePublicCodeChange,

    // Subscription handlers
    handleSubscribe,
    handleUnsubscribe,

    // Schedule handlers
    handleNextStartTime,
    handlePrevStartTime,

    // Element handlers (new flat structure)
    handleAddElement,
    handleUpdateElement,
    handleDeleteElement,
    handleReorderElements,
    handleMoveElement,
    handleUngroupElement,

    // Defaults handlers
    handleSetDefaults,
    handleLoadDefaults,
  } = useTimeline();

  const formatTimeWithoutZeroSeconds = (time24H: string): string => {
    const formatted = formatTo12Hour(parseTimeToSeconds(time24H), true);
    return formatted.replace(/:00 (AM|PM)$/, ' $1');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-gray-900 dark:via-slate-900 dark:to-gray-800 text-gray-900 dark:text-white">
      {loading && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 flex items-center gap-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
            <span className="text-gray-900 dark:text-white">Loading...</span>
          </div>
        </div>
      )}

      {error && (
        <div className="fixed top-0 left-0 right-0 bg-red-600 text-white px-4 py-3 z-50 flex items-center justify-between">
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="text-white hover:text-red-200 font-bold text-xl"
          >
            ×
          </button>
        </div>
      )}

      <TopNav
        timelineName={timelineName}
        onTimelineNameChange={handleTimelineNameChange}
        isPublic={isTimelinePublic}
        onTogglePublic={isOwner ? handleTogglePublic : undefined}
        publicCode={timelinePublicCode ?? undefined}
        onPublicCodeChange={handlePublicCodeChange}
        onSetDefaults={handleSetDefaults}
        onLoadDefaults={handleLoadDefaults}
        isOwner={isOwner}
        timelineData={{ schedule, elements }}
        timelineId={currentTimelineId ?? undefined}
        allTimelines={allTimelines}
        currentTimelineId={currentTimelineId}
        onTimelineSwitch={handleTimelineSwitch}
        onCreateNewTimeline={handleCreateNewTimeline}
        onDeleteTimeline={handleDeleteTimeline}
        canDelete={allTimelines.length > 1}
        isAutoAdvanceEnabled={isAutoAdvanceEnabled}
        onAutoAdvanceChange={setIsAutoAdvanceEnabled}
        autoAdvanceBuffer={autoAdvanceBuffer}
        onAutoAdvanceBufferChange={setAutoAdvanceBuffer}
        timeOffset={timeOffset}
        onTimeOffsetChange={setTimeOffset}
        onUnsubscribe={handleUnsubscribe}
        onSubscribe={handleSubscribe}
        isSubscribed={isSubscribed}
      />

      <div className="container mx-auto px-4 py-8 max-w-5xl">
        {publicCode && (
          <div className="mb-6 max-w-md mx-auto">
            <OpenInAppButton publicCode={publicCode} />
          </div>
        )}

        <div className="mb-10">
          <CountdownTimer
            selectedStartTime={effectiveStartTime}
            occurrenceStartAbsolute={effectiveStartAbsolute}
            elements={elements}
            timeOffset={timeOffset}
            onTimeOffsetChange={setTimeOffset}
            isPublic={isTimelinePublic}
            publicCode={timelinePublicCode}
            noUpcomingOccurrences={noUpcomingOccurrences}
            isOccurrenceComplete={isOccurrencePast}
            dayName={effectiveDayName}
          />
        </div>

        <div className="mb-8">
          <CollapsibleSection
            title="Times"
            icon={<Clock className="w-6 h-6 text-blue-400" />}
            defaultOpen={false}
            titleAction={
              isOwner ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isAutoAdvanceEnabled) {
                      const currentEffective = activeStartTime || clientAutoAdvanceTime || scheduledOccurrenceTimes[0]?.time;
                      if (currentEffective) {
                        // This will trigger through context's selectedStartTime setter
                        handleNextStartTime();
                        handlePrevStartTime();
                      }
                    }
                    setIsAutoAdvanceEnabled(!isAutoAdvanceEnabled);
                  }}
                  className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs transition-colors ${
                    isAutoAdvanceEnabled
                      ? 'bg-blue-600/30 hover:bg-blue-600/50 border border-blue-500/50 text-blue-600 dark:text-blue-400'
                      : 'bg-gray-200/50 dark:bg-gray-700/50 hover:bg-gray-300/50 dark:hover:bg-gray-600/50 border border-gray-300 dark:border-gray-600 text-gray-500'
                  }`}
                  title={isAutoAdvanceEnabled ? 'Click to disable auto-advance' : 'Click to enable auto-advance'}
                >
                  <Zap className="w-3 h-3" />
                  <span>{isAutoAdvanceEnabled ? 'Auto' : 'Auto Off'}</span>
                </button>
              ) : (
                <div
                  className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                    isAutoAdvanceEnabled
                      ? 'bg-blue-600/20 border border-blue-500/30 text-blue-500 dark:text-blue-400'
                      : 'bg-gray-200/30 dark:bg-gray-700/30 border border-gray-300 dark:border-gray-600 text-gray-400'
                  }`}
                  title={isAutoAdvanceEnabled ? 'Auto-advance is enabled' : 'Auto-advance is disabled'}
                >
                  <Zap className="w-3 h-3" />
                  <span>{isAutoAdvanceEnabled ? 'Auto' : 'Auto Off'}</span>
                </div>
              )
            }
            headerContent={
              noUpcomingOccurrences && !isOccurrencePast ? (
                <div className="text-sm text-gray-500 italic">
                  No upcoming times
                </div>
              ) : (
                <div className="flex items-center gap-2 sm:gap-3 md:gap-4 min-w-0 flex-shrink">
                  {!isAutoAdvanceEnabled && (
                    <button
                      onClick={handlePrevStartTime}
                      className="flex items-center gap-2 p-1.5 sm:p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200/50 dark:hover:bg-gray-700/50 rounded-full transition-all duration-300 transform hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={upcomingTimes.length <= 1}
                    >
                      {upcomingTimes.length > 1 && (() => {
                        const currentIndex = upcomingTimes.findIndex(time => time === selectedStartTime);
                        const prevIndex = currentIndex === -1
                          ? upcomingTimes.length - 1
                          : (currentIndex - 1 + upcomingTimes.length) % upcomingTimes.length;
                        return (
                          <span className="text-xs text-gray-500">
                            {formatTimeWithoutZeroSeconds(upcomingTimes[prevIndex])}
                          </span>
                        );
                      })()}
                      <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                  )}

                  <div className="flex flex-col sm:flex-row items-center gap-1.5 sm:gap-2 md:gap-3 min-w-0">
                    <div className="text-lg sm:text-xl md:text-2xl font-extrabold text-gray-900 dark:text-white tracking-tight whitespace-nowrap">
                      {effectiveStartTime && formatTo12Hour(
                        calculateOccurrenceStartTimeFromElements(parseTimeToSeconds(effectiveStartTime), elements),
                        true
                      )}
                    </div>
                    <div className="w-full sm:w-auto sm:hidden border-t border-gray-300 dark:border-gray-600"></div>
                    <div className="hidden sm:block text-lg sm:text-xl md:text-2xl text-gray-500">/</div>
                    <div className="text-base sm:text-lg md:text-xl text-gray-500 whitespace-nowrap">
                      {effectiveStartTime && formatTo12Hour(parseTimeToSeconds(effectiveStartTime), true)}
                    </div>
                  </div>

                  {!isAutoAdvanceEnabled && (
                    <button
                      onClick={handleNextStartTime}
                      className="flex items-center gap-2 p-1.5 sm:p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200/50 dark:hover:bg-gray-700/50 rounded-full transition-all duration-300 transform hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={upcomingTimes.length <= 1}
                    >
                      <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
                      {upcomingTimes.length > 1 && (() => {
                        const currentIndex = upcomingTimes.findIndex(time => time === selectedStartTime);
                        const nextIndex = currentIndex === -1
                          ? 0
                          : (currentIndex + 1) % upcomingTimes.length;
                        return (
                          <span className="text-xs text-gray-500">
                            {formatTimeWithoutZeroSeconds(upcomingTimes[nextIndex])}
                          </span>
                        );
                      })()}
                    </button>
                  )}
                </div>
              )
            }
          >
            <ScheduleChart
              schedule={schedule}
              onScheduleChange={setSchedule}
              snapMinutes={30}
              readOnly={!isOwner}
            />

            {isOwner && isAutoAdvanceEnabled && (
              <div className="mt-4 flex items-center justify-between bg-gray-100/50 dark:bg-gray-800/50 rounded-lg p-3">
                <div className="flex-1">
                  <label className="text-sm text-gray-600 dark:text-gray-300">Buffer after occurrence ends</label>
                  <p className="text-xs text-gray-500">Time before switching to next occurrence</p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    max="60"
                    value={Math.round(autoAdvanceBuffer / 60)}
                    onChange={(e) => setAutoAdvanceBuffer(Math.max(0, parseInt(e.target.value) || 0) * 60)}
                    className="w-16 px-2 py-1 text-sm bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-white text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-500 dark:text-gray-400">min</span>
                </div>
              </div>
            )}
          </CollapsibleSection>
        </div>

        <div className="mb-8">
          <TimeElementList
            elements={elements}
            selectedStartTime={effectiveStartTime}
            occurrenceStartAbsolute={effectiveStartAbsolute}
            timeOffset={timeOffset}
            onAddElement={handleAddElement}
            onUpdateElement={handleUpdateElement}
            onDeleteElement={handleDeleteElement}
            onReorderElements={handleReorderElements}
            onMoveElement={handleMoveElement}
            onUngroupElement={handleUngroupElement}
            timelineId={currentTimelineId}
            noUpcomingOccurrences={noUpcomingOccurrences}
            isOccurrenceComplete={isOccurrencePast}
            dayName={effectiveDayName}
            readOnly={!isOwner}
          />
        </div>

        <div className="text-center text-xs text-gray-600 mt-8 pb-4">
          {process.env.REACT_APP_GIT_COMMIT && (
            <span>v{process.env.REACT_APP_GIT_COMMIT}</span>
          )}
          {process.env.REACT_APP_GIT_BRANCH && process.env.REACT_APP_GIT_BRANCH !== 'HEAD' && (
            <span> ({process.env.REACT_APP_GIT_BRANCH})</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default App;
